/**
 * Toggle — Pressable button with a binary on/off state.
 *
 * Toggle Component Guidelines
 * - Use for binary state buttons (bold, italic, view mode, favorite). For on/off
 *   settings that take effect immediately, prefer Switch instead.
 * - Choose `variant` based on emphasis: `default` for subtle toggles where the
 *   pressed state is secondary, `emphasized` for primary selection (highlighted
 *   with brand color), `empty` for placeholder/add-new states (dashed border).
 * - Set `icon` when the toggle contains only an icon — it makes the button square.
 *   Pair with a Tooltip so the state and action are discoverable.
 * - Use `size="sm"` in toolbars and dense UIs; `size="base"` for standard controls;
 *   `size="lg"` for prominent standalone toggles.
 * @see https://base-ui.com/react/components/toggle Base UI Toggle documentation
 * @see https://ui.shadcn.com/docs/components/toggle shadcn/ui Toggle documentation
 */
import type { LayoutClassName } from "@replohq/lattice/types/layoutClassName";
import type { VariantProps } from "class-variance-authority";

import React from "react";

import { Toggle as TogglePrimitive } from "@base-ui/react/toggle";
import { cn } from "@replohq/lattice/utils/cn";
import { cva } from "class-variance-authority";

const toggleVariants = cva(
  "border border-0.5 border-border bg-sheet text-muted font-medium inline-flex items-center justify-center gap-1.5 shrink-0 select-none outline-none transition-[color,background-color,border-color] duration-150 ease-out hover:bg-button-secondary-hover disabled:pointer-events-auto disabled:opacity-50 disabled:cursor-not-allowed focus-visible:ring-ring/50 focus-visible:ring-3 [&_svg]:pointer-events-none [&_svg]:shrink-0",
  {
    variants: {
      variant: {
        default:
          "data-[pressed]:bg-surface data-[pressed]:text-default data-[pressed]:border-border",
        emphasized:
          "data-[pressed]:bg-selectable-selected data-[pressed]:text-primary data-[pressed]:border-primary",
        empty: "border-1 border-dashed bg-sheet text-muted hover:bg-sheet",
      },
      size: {
        sm: "text-xs [&_svg:not([class*='size-'])]:size-3 rounded-md",
        base: "text-sm [&_svg:not([class*='size-'])]:size-3.5 rounded-lg",
        lg: "text-base [&_svg:not([class*='size-'])]:size-4 rounded-lg",
      },
      icon: {
        true: "",
        false: "",
      },
    },
    compoundVariants: [
      { icon: false, size: "sm", className: "h-6 px-2" },
      { icon: false, size: "base", className: "h-8 px-3" },
      { icon: false, size: "lg", className: "h-10 px-4" },
      { icon: true, size: "sm", className: "size-6 p-1" },
      { icon: true, size: "base", className: "size-8 p-2" },
      { icon: true, size: "lg", className: "size-10 p-3" },
    ],
    defaultVariants: {
      variant: "default",
      size: "base",
      icon: false,
    },
  },
);

interface ToggleProps<ClassName extends string = string>
  extends Omit<TogglePrimitive.Props, "className">,
    VariantProps<typeof toggleVariants> {
  className?: LayoutClassName<ClassName>;
  UNSAFE_className?: string;
}

const ToggleInner = <ClassName extends string>(
  {
    variant = "default",
    size = "base",
    icon = false,
    className,
    UNSAFE_className,
    ...props
  }: ToggleProps<ClassName>,
  ref: React.ForwardedRef<HTMLButtonElement>,
) => {
  return (
    <TogglePrimitive
      ref={ref}
      className={cn(
        toggleVariants({ variant, size, icon }),
        className,
        UNSAFE_className,
      )}
      {...props}
      data-slot="toggle"
    />
  );
};

// Legacy cast - may or may not be necessary
// eslint-disable-next-line replo/no-as-cast
const Toggle = React.forwardRef(ToggleInner) as <ClassName extends string>(
  props: ToggleProps<ClassName> & React.RefAttributes<HTMLButtonElement>,
) => React.ReactNode;

export { Toggle };
export type { ToggleProps };
